import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Lede, Section, EmptyState,
} from "../components/Primitives";
import { CrisisPanel } from "./Booking";
import { LEGAL_PAGES } from "../content/legal";
import { LEGAL_NAV } from "../content/nav";
import { useSEO } from "../lib/seo";

export default function Legal() {
  const { slug } = useParams();
  const page = LEGAL_PAGES[slug];

  useSEO({
    title: page ? page.title : "Legal",
    description: page?.meta,
    canonical: `/legal/${slug}`,
  });

  if (!page) {
    return (
      <Container className="py-20">
        <EmptyState title="No such legal page">
          <Link to="/legal/terms" className="link-u">Start with our Terms →</Link>
        </EmptyState>
      </Container>
    );
  }

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Legal" }, { label: page.title }]} />
      </Container>

      <Section className="pb-20">
        <Container className="grid items-start gap-12 lg:grid-cols-[1fr_230px]">
          <article>
            {page.updated && <Eyebrow>{page.updated}</Eyebrow>}
            <H1 className="mt-4">{page.title}</H1>
            {page.lede && <Lede className="mt-6">{page.lede}</Lede>}

            {page.crisis && (
              <>
                <div className="mt-8 border-l-2 border-l-alert bg-surface p-5">
                  <p className="ui text-sm font-semibold text-ink">
                    If you are having thoughts of harming yourself or someone else, or you are in
                    immediate danger, please do not use this service. Contact one of the following now.
                  </p>
                </div>
                <div className="mt-4"><CrisisPanel /></div>
              </>
            )}

            <div className="mt-10 space-y-8">
              {page.sections.map((s, i) => (
                <section key={`${s.h || s.part || "sec"}-${i}`}>
                  {s.part && (
                    <p className="eyebrow mt-6 border-t border-rule pt-8">{s.part}</p>
                  )}
                  {s.h && <H2 className="h2-sub">{s.h}</H2>}
                  {s.p && <p className="measure ui mt-3 text-sm leading-relaxed text-dim">{s.p}</p>}
                  {s.list && (
                    <ul className="ui mt-3 space-y-2 text-sm text-dim">
                      {s.list.map((x) => <li key={x}>· {x}</li>)}
                    </ul>
                  )}
                  {s.table && (
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full min-w-[560px] text-sm">
                        <thead>
                          <tr className="border-b border-rule">
                            {s.table.head.map((h) => (
                              <th key={h} scope="col" className="ui py-2 pr-4 text-left text-xs uppercase tracking-wider text-dim">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.table.rows.map((row, ri) => (
                            <tr key={ri} className={ri % 2 ? "bg-wash" : ""}>
                              {row.map((cell, ci) => (
                                <td key={ci} className="ui border-b border-rule px-0 py-2.5 pr-4 align-top text-dim">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              ))}
            </div>

            <div className="card-s mt-14 p-6">
              <H3>These are drafts for your lawyer</H3>
              <p className="mt-2 text-[15px] leading-[1.75] text-dim">
                Written against the Indian statutory framework — the Consumer Protection Act 2019 and its
                E-Commerce Rules 2020, the Drugs and Magic Remedies (Objectionable Advertisements) Act
                1954, the CCPA Guidelines of 2022 and 2023, the DPDP Act 2023 and Rules 2025, the IT Rules
                2021 and the RCI and NCAHP Acts. They must still be reviewed by a qualified Indian lawyer,
                and separately by UK and US counsel if consultations are actively marketed into those
                markets before publication.
              </p>
            </div>
          </article>

          <aside className="lg:sticky lg:top-28 lg:self-start" data-testid="legal-nav">
            <p className="eyebrow mb-3">All legal pages</p>
            <ul className="space-y-1.5">
              {LEGAL_NAV.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className={`ui block text-sm transition-colors ${
                      l.to === `/legal/${slug}` ? "text-gold" : "text-dim hover:text-gold"
                    }`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        </Container>
      </Section>
    </>
  );
}
